<script lang="ts" setup>
import logout from "~/features/auth/logout";
import ArrowOutIcon from '~/assets/icons/arrows/arrowOutThin.svg?component'
import ArrowRightIcon from '~/assets/icons/arrows/24/chevron-right.svg?component'
import ExitIcon from '~/assets/icons/exit.svg?component'
import NavigationManager from '~/widgets/layout/account/components/NavigationManager.vue'
import NavigationInfo from '~/widgets/layout/account/components/NavigationInfo.vue'
import AppButton from '~/components/ui/buttons/AppButton.vue'
import useAccountStore from '~/shared/store/modules/account'
import config from '~/shared/config'
import { useMonitorSize } from '~/composables/monitor-size'
import { useWindowScroll } from '~/composables/scroll-event'
import type { IScrollInfo } from '~/shared/interfaces/composables'
import type { IMonitorSize } from '~/shared/interfaces/composables'
import type { IBanner } from '~/shared/interfaces/banners'
import useCompaniesStore from "~/shared/store/modules/account/companies";

defineProps<{
  isMainAccountPage: boolean,
}>();

const accountStore = useAccountStore()
const profile = accountStore.userProfile

const companies = useCompaniesStore();

const banner = ref<IBanner[]>([]);
let scrollInfo: IScrollInfo | null = null;
let sizes: IMonitorSize | null = useMonitorSize()

const getAddressDetails = computed(() => {
  if (profile && profile.deliveryAddress) {
    const { office, floor, entrance, doorPhone } = profile.deliveryAddress;

    if (office || floor || entrance || doorPhone) {
      let addressDetails = [];
      if (office) {
        addressDetails.push(`офис ${office}`);
      }
      if (floor) {
        addressDetails.push(`этаж ${floor}`);
      }
      if (entrance) {
        addressDetails.push(`подъезд ${entrance}`);
      }
      if (doorPhone) {
        addressDetails.push(`домофон ${doorPhone}`);
      }
      return addressDetails.join(", ")
    }
  }
  return '';
})

const getCompanyInfo = computed(() => {
  let companyInfo = '';
  let companyName = '';
  const mainCompany = companies.getMainCompany;
  if (mainCompany) {
    companyInfo = `${mainCompany.kpp ? `ИНН/КПП: ${mainCompany.inn}/${mainCompany.kpp}` : `ИНН: ${mainCompany.inn}`}`;
    companyName = mainCompany.companyName
  }
  return { companyInfo, companyName }
})

async function getBanner() {
  const type = "account"
  const { data } = await useFetch(`/api/banners/${type}`)
  if (data.value) {
    banner.value = data.value
  }
}

await getBanner()

if (process.client) {
  scrollInfo = useWindowScroll()
}
</script>

<template>
  <ClientOnly>
    <div
      class="account-name"
      v-if="sizes && (!sizes.isMobile || (sizes.isMobile && isMainAccountPage)) && profile"
    >
      {{profile.user.lastName}} {{profile.user.firstName}}
    </div>
    <nav
      v-if="sizes && (!sizes.isMobile || (sizes.isMobile && isMainAccountPage))"
      class="account-navigation"
      :style="{top: scrollInfo?.isScrollDown ? `${108}px` : `${156}px`}"
    >
      <ul class="account-navigation__list">
        <template v-for="(accountLink) in accountStore.accountNavigationList">
          <li
            v-if="!sizes.isMobile || accountLink.value != 'account'"
            v-show="Object.keys(accountLink).includes('show') ? accountLink.show : true"
            class="account-navigation__list-item"
          >
            <NuxtLink
              class="account-navigation__list-link"
              :to="accountLink.path"
              :class="$route.meta.key == accountLink.value && 'active'"
            >
              <div class="account-navigation__list-content">
                <span>{{ accountLink.name }}</span>
                <ArrowRightIcon v-if="sizes.isMobile" />
              </div>

              <template v-if="sizes.isMobile">
                <NavigationManager v-if="accountLink.value == 'managers'"/>
                <NavigationInfo
                  v-if="accountLink.value == 'addresses' && profile && profile.deliveryAddress.address.length"
                  :text="profile.deliveryAddress.address"
                  :sub-text="getAddressDetails"
                />
                <NavigationInfo
                  v-if="accountLink.value == 'companies' && companies.companies.length"
                  :text="getCompanyInfo.companyName"
                  :sub-text="getCompanyInfo.companyInfo"
                />
              </template>
            </NuxtLink>
          </li>
        </template>
      </ul>
      <div
        class="account-navigation__logout"
        v-if="sizes.isMobile"
        @click="logout"
      >
        <AppButton
          text="Выйти из профиля"
          :icon="ExitIcon"
          type="ghost"
          sub-type="negative"
        />
      </div>
      <div
        class="account-navigation__admin"
        v-if="accountStore.userRole === 'admin'"
      >
        <AppButton
          type="black"
          text="Админ-панель"
          :icon="ArrowOutIcon"
          direction="row-reverse"
          @click="navigateTo(config().manageUrl, { external: true })"
        />
      </div>
      <div v-if="banner.length > 0 && sizes.browserWidth > 1200" class="account-navigation__banner">

      <NuxtLink :to="banner[0].link" :target="banner[0].target_blank ? '_blank' : '_self'">
          <ClientOnly>
            <nuxt-img :src="banner[0].image" alt="Баннер Продукции" loading="lazy" width="195"/>
          </ClientOnly>
        </NuxtLink>
      </div>
    </nav>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.account {
  &-name {
    display: none;

    @include md-breakpoint {
      display: block;
      padding: 8px 20px 0;
      @include font(20px, 40px, 600);
    }
  }

  &-navigation {
    position: sticky;
    align-self: flex-start;
    top: 108px;
    transition: .2s top;
    flex-shrink: 0;
    width: 195px;

    @include md-breakpoint {
      width: auto;
      padding-bottom: 40px;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 24px;

      &-link {
        color: $content_primary;
        transition: .3s color;
        @include font(16px, 20px);

        &.active {
          color: $content_orange;
          @include font(16px, 20px, 500);
        }

        @include mf-breakpoint {
          &:hover {
            color: $content_orange;
          }
        }

        @include md-breakpoint {
          padding: 16px 20px;
        }
      }

      &-content {
        @include md-breakpoint {
          display: flex;
          justify-content: space-between;
          align-items: center;
          @include font(16px, 20px, 500);
        }
      }

      &-item {
        position: relative;

        @include md-breakpoint {
          &:not(:last-child) {
            &::after {
              content: "";
              position: absolute;
              bottom: 0;
              left: 50%;
              transform: translateX(-50%);
              width: calc(100% - 40px);
              height: 1px;
              background: $bg_border;
            }
          }
        }
      }

      @include md-breakpoint {
        border-radius: 12px;
        gap: 0;
        background: $bg_page;
      }
    }

    &__banner {
      margin-top: 24px;
      overflow: hidden;
      border-radius: 16px;
    }

    &__logout {
      margin-top: 16px;
      padding: 16px 20px;
      background-color: $bg_page;
      border-radius: 12px;

      .button {
        padding: 0;
      }
    }

    &__admin {
      padding: 28px 0 0;

      @include md-breakpoint {
        padding: 24px 20px 0;
      }

      .button {
        width: 100%;
      }
    }

    &__link {
      margin-top: 32px;
      width: 196px;
      height: 48px;

      @include md-breakpoint {
        margin: 28px auto 40px;
        width: calc(100% - 40px);
      }
    }

    @include md-breakpoint {
      width: 100%;
    }
  }
}
</style>
